import type { NavItem, NavSection, NavWorkflowGroup } from '@/components/layout/Sidebar';
import { masterAdminNav, orgAdminNav, posNav } from '@/config/navigation';
import { SUBSCRIPTION_VALIDATION_ENABLED } from '@/config/subscriptionPolicy';
import { getAppNavConfig } from '@/config/appNavigation';
import { filterNavItemsByEntryModule } from '@/config/moduleEntries';
import {
  dedupeNavItems,
  resolveModuleAdminRouteFeature,
  resolveModuleAdminRoutePermission,
} from '@/config/moduleDefaults';
import { isOrgOwner } from '@/utils/moduleAccess';
import { ORG_ROUTE_FEATURES } from '@/config/routeFeatures';
import { withModuleScopedMasterRoutes } from '@/config/moduleSharedPaths';
import type { ErpModule } from '@/api/platform.api';

/** @deprecated Legacy flat sections — use getAppWorkflowNav */
export { orgNavSections } from '@/config/orgNavSections';

const MANAGE_PERMISSIONS = [
  'ORG_MANAGE',
  'STORE_MANAGE',
  'EMPLOYEE_MANAGE',
  'PRODUCT_MANAGE',
  'INVENTORY_MANAGE',
  'ROLE_MANAGE',
  'SUBSCRIPTION_MANAGE',
];

export function isPosOnlyUser(permissions: string[]): boolean {
  if (!permissions.includes('BILLING_CREATE')) return false;
  return !MANAGE_PERMISSIONS.some((p) => permissions.includes(p));
}

export function filterNavByPermissions(items: NavItem[], has: (code: string) => boolean): NavItem[] {
  const rules = withModuleScopedMasterRoutes({
    '/org/profile': 'ORG_VIEW',
    '/pos/stores': 'STORE_VIEW',
    '/org/employees': 'EMPLOYEE_VIEW',
    '/org/roles': 'ROLE_VIEW',
    '/pos/products': 'PRODUCT_VIEW',
    '/pos/categories': 'PRODUCT_VIEW',
    '/pos/brands': 'PRODUCT_VIEW',
    '/pos/units': 'PRODUCT_VIEW',
    '/org/taxes': 'PRODUCT_VIEW',
    '/pos/tax-rules': 'PRODUCT_VIEW',
    '/pos/store-products': 'PRODUCT_VIEW',
    '/pos/invoices': 'BILLING_VIEW',
    '/pos/inventory': 'INVENTORY_VIEW',
    '/pos/inventory/ledger': 'INVENTORY_VIEW',
    '/pos/suppliers': 'INVENTORY_VIEW',
    '/procurement': 'PROCUREMENT_VIEW',
    '/procurement/purchase-requests': 'PROCUREMENT_VIEW',
    '/procurement/rfqs': 'PROCUREMENT_VIEW',
    '/procurement/quotations': 'PROCUREMENT_VIEW',
    '/procurement/purchase-orders': 'PROCUREMENT_VIEW',
    '/procurement/goods-receipts': 'PROCUREMENT_RECEIVE',
    '/procurement/vendor-bills': 'PROCUREMENT_VENDOR_BILL',
    '/procurement/approvals': ['PROCUREMENT_REQUEST_APPROVE', 'PROCUREMENT_PO_APPROVE'],
    '/procurement/reports': 'PROCUREMENT_ANALYTICS_VIEW',
    '/procurement/setup': 'PROCUREMENT_MANAGE',
    '/procurement/contracts': 'PROCUREMENT_VIEW',
    '/procurement/catalogs': 'PROCUREMENT_VIEW',
    '/pos/customers': 'CUSTOMER_VIEW',
    '/crm': 'CRM_VIEW',
    '/crm/leads': 'CRM_VIEW',
    '/crm/contacts': 'CRM_VIEW',
    '/crm/companies': 'CRM_VIEW',
    '/crm/deals': 'CRM_VIEW',
    '/crm/pipelines': 'CRM_PIPELINE_MANAGE',
    '/crm/followups': 'CRM_VIEW',
    '/crm/tasks': 'CRM_VIEW',
    '/crm/campaigns': 'CRM_CAMPAIGN_MANAGE',
    '/crm/analytics': 'CRM_ANALYTICS_VIEW',
    '/crm/reports': 'CRM_ANALYTICS_VIEW',
    '/crm/setup': 'CRM_MANAGE',
    '/lead-gen': 'LEAD_GEN_VIEW',
    '/lead-gen/forms': 'LEAD_GEN_MANAGE',
    '/lead-gen/landing-pages': 'LEAD_GEN_MANAGE',
    '/lead-gen/campaigns': 'LEAD_GEN_CAMPAIGN',
    '/lead-gen/visitor-tracking': 'LEAD_GEN_VIEW',
    '/lead-gen/scoring': 'LEAD_GEN_MANAGE',
    '/lead-gen/routing': 'LEAD_GEN_MANAGE',
    '/lead-gen/ab-tests': 'LEAD_GEN_MANAGE',
    '/lead-gen/webhooks': 'LEAD_GEN_VIEW',
    '/lead-gen/enrichment': 'LEAD_GEN_MANAGE',
    '/lead-gen/consent': 'LEAD_GEN_VIEW',
    '/lead-gen/reports': 'LEAD_GEN_ANALYTICS',
    '/lead-gen/sources': 'LEAD_GEN_VIEW',
    '/social': 'SOCIAL_VIEW',
    '/social/inbox': 'SOCIAL_INBOX_REPLY',
    '/social/channels': 'SOCIAL_CHANNEL_MANAGE',
    '/social/publishing': 'SOCIAL_PUBLISH',
    '/social/calendar': 'SOCIAL_PUBLISH',
    '/social/analytics': 'SOCIAL_ANALYTICS_VIEW',
    '/social/automation': 'SOCIAL_VIEW',
    '/social/crm-leads': 'SOCIAL_VIEW',
    '/social/teams': 'SOCIAL_VIEW',
    '/social/comments': 'SOCIAL_INBOX_REPLY',
    '/social/connect': 'SOCIAL_CHANNEL_MANAGE',
    '/social/settings': 'SOCIAL_MANAGE',
    '/social/platforms': 'SOCIAL_VIEW',
    '/social/approvals': 'SOCIAL_PUBLISH',
    '/social/webhooks': 'SOCIAL_VIEW',
    '/social/listening': 'SOCIAL_VIEW',
    '/social/campaigns': 'SOCIAL_CAMPAIGN_MANAGE',
    '/social/reviews': 'SOCIAL_REVIEW_MANAGE',
    '/social/reports': 'SOCIAL_ANALYTICS_VIEW',
    '/social/setup': 'SOCIAL_MANAGE',
    '/hr': 'HR_VIEW',
    '/hr/employees': 'HR_EMPLOYEE_MANAGE',
    '/hr/attendance': 'HR_ATTENDANCE_MANAGE',
    '/hr/leaves': 'HR_LEAVE_MANAGE',
    '/hr/approvals': 'HR_APPROVAL_MANAGE',
    '/hr/payroll': 'HR_PAYROLL_MANAGE',
    '/hr/recruitment': 'HR_RECRUITMENT_MANAGE',
    '/hr/appraisal': 'HR_MANAGE',
    '/hr/claims': 'HR_CLAIM_MANAGE',
    '/hr/training': 'HR_TRAINING_MANAGE',
    '/hr/reports': 'HR_ANALYTICS_VIEW',
    '/hr/setup': 'HR_MANAGE',
    '/desk': 'DESK_VIEW',
    '/desk/tickets': 'DESK_VIEW',
    '/desk/inbox': 'DESK_VIEW',
    '/desk/livechat': 'DESK_VIEW',
    '/desk/projects': 'DESK_PROJECT_MANAGE',
    '/desk/boards': 'DESK_PROJECT_MANAGE',
    '/desk/knowledgebase': 'DESK_KB_MANAGE',
    '/desk/reports': 'DESK_ANALYTICS_VIEW',
    '/desk/automation': 'DESK_AUTOMATION_MANAGE',
    '/desk/setup': 'DESK_WORKFLOW_MANAGE',
    '/desk/setup/workflows': 'DESK_WORKFLOW_MANAGE',
    '/desk/canned-responses': 'DESK_WORKFLOW_MANAGE',
    '/finance': 'FINANCE_VIEW',
    '/finance/receivables': 'FINANCE_VIEW',
    '/finance/payables': 'FINANCE_VIEW',
    '/finance/payables/ap': 'FINANCE_AP_MANAGE',
    '/finance/tax': 'FINANCE_VIEW',
    '/finance/control': 'FINANCE_VIEW',
    '/finance/setup/wizard': 'FINANCE_SETTINGS_MANAGE',
    '/finance/health': 'FINANCE_VIEW',
    '/finance/close': 'FINANCE_MANAGE',
    '/finance/control/rules': 'FINANCE_SETTINGS_MANAGE',
    '/finance/treasury/forecast': 'FINANCE_TREASURY_MANAGE',
    '/finance/collections': 'FINANCE_INVOICE_MANAGE',
    '/finance/automation': 'FINANCE_MANAGE',
    '/finance/analytics': 'FINANCE_REPORT_VIEW',
    '/finance/compliance': 'FINANCE_TAX_MANAGE',
    '/finance/audit': 'FINANCE_MANAGE',
    '/finance/documents': 'FINANCE_AP_MANAGE',
    '/finance/integrations': 'FINANCE_VIEW',
    '/finance/accounting': 'FINANCE_MANAGE',
    '/finance/ledger': 'FINANCE_JOURNAL_MANAGE',
    '/finance/invoices': 'FINANCE_INVOICE_MANAGE',
    '/finance/payments': 'FINANCE_PAYMENT_MANAGE',
    '/finance/banking': 'FINANCE_BANKING_MANAGE',
    '/finance/taxes': 'FINANCE_TAX_MANAGE',
    '/finance/expenses': 'FINANCE_EXPENSE_MANAGE',
    '/finance/assets': 'FINANCE_ASSET_MANAGE',
    '/finance/budgets': 'FINANCE_MANAGE',
    '/finance/treasury': 'FINANCE_TREASURY_MANAGE',
    '/finance/revenue': 'FINANCE_MANAGE',
    '/finance/workflows': 'FINANCE_SETTINGS_MANAGE',
    '/finance/workflows/designer': 'FINANCE_SETTINGS_MANAGE',
    '/finance/reports': 'FINANCE_REPORT_VIEW',
    '/finance/reports/builder': 'FINANCE_REPORT_VIEW',
    '/finance/setup': 'FINANCE_SETTINGS_MANAGE',
    '/projects': 'PROJECT_VIEW',
    '/projects/list': 'PROJECT_VIEW',
    '/projects/tasks': 'PROJECT_TASK_MANAGE',
    '/projects/boards': 'PROJECT_VIEW',
    '/projects/sprints': 'PROJECT_SPRINT_MANAGE',
    '/projects/timesheets': 'PROJECT_TIMESHEET_MANAGE',
    '/projects/portfolio': 'PROJECT_PORTFOLIO_MANAGE',
    '/projects/issues': 'PROJECT_ISSUE_MANAGE',
    '/projects/gantt': 'PROJECT_VIEW',
    '/projects/resources': 'PROJECT_MANAGE',
    '/projects/calendar': 'PROJECT_VIEW',
    '/projects/workload': 'PROJECT_ANALYTICS_VIEW',
    '/projects/reports': 'PROJECT_ANALYTICS_VIEW',
    '/projects/automation': 'PROJECT_AUTOMATION_MANAGE',
    '/projects/setup': 'PROJECT_MANAGE',
    '/pos/discounts': 'PRODUCT_VIEW',
    '/pos/product-alerts': 'INVENTORY_VIEW',
    '/pos/reports': 'REPORT_VIEW',
    '/pos/terminal-reports': 'REPORT_VIEW',
    '/warehouse': 'WAREHOUSE_VIEW',
    '/warehouse/operations': 'WAREHOUSE_VIEW',
    '/warehouse/mobile': 'WAREHOUSE_VIEW',
    '/warehouse/setup': 'WAREHOUSE_ADMIN',
    '/warehouse/picks': 'WAREHOUSE_PICK',
    '/warehouse/transfers': 'WAREHOUSE_TRANSFER',
    '/warehouse/cycle-counts': 'WAREHOUSE_VIEW',
    '/warehouse/waves': 'WAREHOUSE_PICK',
    '/warehouse/dock': 'WAREHOUSE_VIEW',
    '/warehouse/cross-dock': 'WAREHOUSE_RECEIVE',
    '/warehouse/labor': 'WAREHOUSE_VIEW',
    '/org/subscription': 'SUBSCRIPTION_MANAGE',
    '/org/settings': 'ORG_MANAGE',
    '/org/administration': 'ORG_MANAGE',
    '/org/workspace/settings': null,
    '/org/workspace/subscription': null,
    '/pos/dashboard': ['DASHBOARD_VIEW', 'BILLING_VIEW'],
    '/pos': 'BILLING_CREATE',
    '/pos/held': 'BILLING_VIEW',
  });

  return items.filter((item) => {
    const path = item.to.split('?')[0];
    const rule = rules[path] ?? rules[item.to];
    if (rule === undefined || rule === null) return true;
    if (Array.isArray(rule)) return rule.some(has);
    return has(rule);
  });
}

export function filterNavByFeatures(items: NavItem[], hasFeature: (code: string) => boolean): NavItem[] {
  if (!SUBSCRIPTION_VALIDATION_ENABLED) {
    return items;
  }
  return items.filter((item) => {
    if (!item.feature) return true;
    return hasFeature(item.feature);
  });
}

/** Default-enabled modules when API has no row (aligned with backend ErpModuleDefaults). */
const DEFAULT_ENABLED_MODULES: ErpModule[] = [
  'POS',
  'INVENTORY',
  'CRM',
  'LOYALTY',
  'REPORTING',
  'PROCUREMENT',
  'ANALYTICS',
  'WAREHOUSE',
];

export function getAppWorkflowNav(
  activeModule: ErpModule,
  has: (code: string) => boolean,
  permissions: string[] = [],
): NavWorkflowGroup[] {
  const config = getAppNavConfig(activeModule);
  if (!config) return [];

  const owner = isOrgOwner(permissions);

  return config.groups
    .filter((group) => {
      if (group.id === 'administration') return owner;
      return true;
    })
    .map((group) => {
      let items = dedupeNavItems(group.items);
      items = filterNavItemsByEntryModule(items, activeModule);
      items = filterNavByPermissions(items, has);
      return { ...group, items };
    })
    .filter((group) => group.items.length > 0);
}

/** @deprecated Use getAppWorkflowNav — returns single-app workflow groups only */
export function getOrgNavSections(
  has: (code: string) => boolean,
  permissions: string[] = [],
  _isModuleEnabled: (module: ErpModule) => boolean,
  entryModule: ErpModule | null = null,
): NavSection[] {
  if (!entryModule) return [];
  const groups = getAppWorkflowNav(entryModule, has, permissions);
  return groups.map((g) => ({ id: g.id, label: g.label, module: entryModule, items: g.items }));
}

/** @deprecated Flat list — prefer getOrgNavSections */
export function getOrgNav(
  has: (code: string) => boolean,
  permissions: string[] = [],
  hasFeature: (code: string) => boolean = () => true,
): NavItem[] {
  if (isPosOnlyUser(permissions)) {
    return [];
  }
  const byPerm = filterNavByPermissions(orgAdminNav, has);
  return filterNavByFeatures(byPerm, hasFeature);
}

export function createEntryModuleChecker(entryModule: ErpModule): (module: ErpModule) => boolean {
  return (module: ErpModule) => module === entryModule;
}

export function createModuleEnabledChecker(
  modules: { module: ErpModule; enabled: boolean }[],
  authModules: string[] = [],
): (module: ErpModule) => boolean {
  return (module: ErpModule) => {
    const row = modules.find((m) => m.module === module);
    if (row) return row.enabled;
    if (authModules.length > 0) return authModules.includes(module);
    return DEFAULT_ENABLED_MODULES.includes(module);
  };
}

export function getPosNav(
  has: (code: string) => boolean,
  hasFeature: (code: string) => boolean = () => true,
): NavItem[] {
  const byPerm = filterNavByPermissions(posNav, has);
  return filterNavByFeatures(byPerm, hasFeature);
}

export function getMasterNav(): NavItem[] {
  return masterAdminNav;
}

export function resolveRouteFeature(pathname: string): string | null {
  const adminFeature = resolveModuleAdminRouteFeature(pathname);
  if (adminFeature !== undefined) {
    return adminFeature;
  }
  if (ORG_ROUTE_FEATURES[pathname] !== undefined) {
    const rule = ORG_ROUTE_FEATURES[pathname];
    return typeof rule === 'string' ? rule : null;
  }
  const prefix = Object.keys(ORG_ROUTE_FEATURES)
    .filter((path) => path !== '/pos' && pathname.startsWith(path + '/'))
    .sort((a, b) => b.length - a.length)[0];
  if (!prefix) {
    return null;
  }
  const rule = ORG_ROUTE_FEATURES[prefix];
  return typeof rule === 'string' ? rule : null;
}

const BASE_ORG_ROUTE_PERMISSION_RULES: Record<string, string | string[] | null> = {
  '/pos/dashboard': ['DASHBOARD_VIEW', 'BILLING_VIEW'],
  '/org/profile': 'ORG_VIEW',
  '/pos/stores': 'STORE_VIEW',
  '/org/employees': 'EMPLOYEE_VIEW',
  '/org/roles': 'ROLE_VIEW',
  '/pos/products': 'PRODUCT_VIEW',
  '/pos/categories': 'PRODUCT_VIEW',
  '/pos/brands': 'PRODUCT_VIEW',
  '/pos/units': 'PRODUCT_VIEW',
  '/org/taxes': 'PRODUCT_VIEW',
  '/pos/tax-rules': 'PRODUCT_VIEW',
  '/pos/store-products': 'PRODUCT_VIEW',
  '/pos/invoices': 'BILLING_VIEW',
  '/pos/inventory': 'INVENTORY_VIEW',
  '/warehouse': 'WAREHOUSE_VIEW',
  '/warehouse/operations': 'WAREHOUSE_VIEW',
  '/warehouse/mobile': 'WAREHOUSE_VIEW',
  '/warehouse/setup': 'WAREHOUSE_ADMIN',
  '/warehouse/picks': 'WAREHOUSE_PICK',
  '/warehouse/transfers': 'WAREHOUSE_TRANSFER',
  '/warehouse/cycle-counts': 'WAREHOUSE_VIEW',
  '/warehouse/waves': 'WAREHOUSE_PICK',
  '/warehouse/dock': 'WAREHOUSE_VIEW',
  '/warehouse/cross-dock': 'WAREHOUSE_RECEIVE',
  '/warehouse/labor': 'WAREHOUSE_VIEW',
  '/pos/product-alerts': 'INVENTORY_VIEW',
  '/pos/suppliers': 'INVENTORY_VIEW',
  '/procurement': 'PROCUREMENT_VIEW',
  '/procurement/purchase-requests': 'PROCUREMENT_VIEW',
  '/procurement/rfqs': 'PROCUREMENT_VIEW',
  '/procurement/quotations': 'PROCUREMENT_VIEW',
  '/procurement/purchase-orders': 'PROCUREMENT_VIEW',
  '/procurement/goods-receipts': 'PROCUREMENT_RECEIVE',
  '/procurement/vendor-bills': 'PROCUREMENT_VENDOR_BILL',
  '/procurement/approvals': ['PROCUREMENT_REQUEST_APPROVE', 'PROCUREMENT_PO_APPROVE'],
  '/procurement/reports': 'PROCUREMENT_ANALYTICS_VIEW',
  '/procurement/setup': 'PROCUREMENT_MANAGE',
  '/procurement/contracts': 'PROCUREMENT_VIEW',
  '/procurement/catalogs': 'PROCUREMENT_VIEW',
  '/pos/customers': 'CUSTOMER_VIEW',
  '/crm': 'CRM_VIEW',
  '/crm/leads': 'CRM_VIEW',
  '/crm/contacts': 'CRM_VIEW',
  '/crm/companies': 'CRM_VIEW',
  '/crm/deals': 'CRM_VIEW',
  '/crm/pipelines': 'CRM_PIPELINE_MANAGE',
  '/crm/followups': 'CRM_VIEW',
  '/crm/tasks': 'CRM_VIEW',
  '/crm/campaigns': 'CRM_CAMPAIGN_MANAGE',
  '/crm/analytics': 'CRM_ANALYTICS_VIEW',
  '/crm/reports': 'CRM_ANALYTICS_VIEW',
  '/crm/setup': 'CRM_MANAGE',
  '/lead-gen': 'LEAD_GEN_VIEW',
  '/lead-gen/forms': 'LEAD_GEN_MANAGE',
  '/lead-gen/landing-pages': 'LEAD_GEN_MANAGE',
  '/lead-gen/campaigns': 'LEAD_GEN_CAMPAIGN',
  '/lead-gen/visitor-tracking': 'LEAD_GEN_VIEW',
  '/lead-gen/scoring': 'LEAD_GEN_MANAGE',
  '/lead-gen/routing': 'LEAD_GEN_MANAGE',
  '/lead-gen/ab-tests': 'LEAD_GEN_MANAGE',
  '/lead-gen/webhooks': 'LEAD_GEN_VIEW',
  '/lead-gen/enrichment': 'LEAD_GEN_MANAGE',
  '/lead-gen/consent': 'LEAD_GEN_VIEW',
  '/lead-gen/reports': 'LEAD_GEN_ANALYTICS',
  '/lead-gen/sources': 'LEAD_GEN_VIEW',
  '/social': 'SOCIAL_VIEW',
  '/social/inbox': ['SOCIAL_VIEW', 'SOCIAL_INBOX_REPLY'],
  '/social/channels': 'SOCIAL_CHANNEL_MANAGE',
  '/social/publishing': 'SOCIAL_PUBLISH',
  '/social/approvals': 'SOCIAL_PUBLISH',
  '/social/webhooks': 'SOCIAL_VIEW',
  '/social/calendar': 'SOCIAL_PUBLISH',
  '/social/listening': 'SOCIAL_VIEW',
  '/social/campaigns': 'SOCIAL_CAMPAIGN_MANAGE',
  '/social/reviews': 'SOCIAL_REVIEW_MANAGE',
  '/social/reports': 'SOCIAL_ANALYTICS_VIEW',
  '/social/setup': 'SOCIAL_MANAGE',
  '/hr': 'HR_VIEW',
  '/hr/employees': 'HR_EMPLOYEE_MANAGE',
  '/hr/attendance': 'HR_ATTENDANCE_MANAGE',
  '/hr/leaves': 'HR_LEAVE_MANAGE',
  '/hr/approvals': 'HR_APPROVAL_MANAGE',
  '/hr/payroll': 'HR_PAYROLL_MANAGE',
  '/hr/recruitment': 'HR_RECRUITMENT_MANAGE',
  '/hr/appraisal': 'HR_MANAGE',
  '/hr/claims': 'HR_CLAIM_MANAGE',
  '/hr/training': 'HR_TRAINING_MANAGE',
  '/hr/reports': 'HR_ANALYTICS_VIEW',
  '/hr/setup': 'HR_MANAGE',
  '/desk': 'DESK_VIEW',
  '/desk/tickets': 'DESK_VIEW',
  '/desk/inbox': 'DESK_VIEW',
  '/desk/livechat': 'DESK_VIEW',
  '/desk/projects': 'DESK_PROJECT_MANAGE',
  '/desk/boards': 'DESK_PROJECT_MANAGE',
  '/desk/knowledgebase': 'DESK_KB_MANAGE',
  '/desk/reports': 'DESK_ANALYTICS_VIEW',
  '/desk/automation': 'DESK_AUTOMATION_MANAGE',
  '/desk/setup': 'DESK_WORKFLOW_MANAGE',
  '/desk/setup/workflows': 'DESK_WORKFLOW_MANAGE',
  '/desk/canned-responses': 'DESK_WORKFLOW_MANAGE',
  '/finance': 'FINANCE_VIEW',
  '/finance/receivables': 'FINANCE_VIEW',
  '/finance/payables': 'FINANCE_VIEW',
  '/finance/payables/ap': 'FINANCE_AP_MANAGE',
  '/finance/tax': 'FINANCE_VIEW',
  '/finance/control': 'FINANCE_VIEW',
  '/finance/setup/wizard': 'FINANCE_SETTINGS_MANAGE',
  '/finance/health': 'FINANCE_VIEW',
  '/finance/close': 'FINANCE_MANAGE',
  '/finance/control/rules': 'FINANCE_SETTINGS_MANAGE',
  '/finance/treasury/forecast': 'FINANCE_TREASURY_MANAGE',
  '/finance/collections': 'FINANCE_INVOICE_MANAGE',
  '/finance/automation': 'FINANCE_MANAGE',
  '/finance/analytics': 'FINANCE_REPORT_VIEW',
  '/finance/compliance': 'FINANCE_TAX_MANAGE',
  '/finance/audit': 'FINANCE_MANAGE',
  '/finance/documents': 'FINANCE_AP_MANAGE',
  '/finance/integrations': 'FINANCE_VIEW',
  '/finance/accounting': 'FINANCE_MANAGE',
  '/finance/ledger': 'FINANCE_JOURNAL_MANAGE',
  '/finance/invoices': 'FINANCE_INVOICE_MANAGE',
  '/finance/payments': 'FINANCE_PAYMENT_MANAGE',
  '/finance/banking': 'FINANCE_BANKING_MANAGE',
  '/finance/taxes': 'FINANCE_TAX_MANAGE',
  '/finance/expenses': 'FINANCE_EXPENSE_MANAGE',
  '/finance/assets': 'FINANCE_ASSET_MANAGE',
  '/finance/budgets': 'FINANCE_MANAGE',
  '/finance/treasury': 'FINANCE_TREASURY_MANAGE',
  '/finance/revenue': 'FINANCE_MANAGE',
  '/finance/workflows': 'FINANCE_SETTINGS_MANAGE',
  '/finance/workflows/designer': 'FINANCE_SETTINGS_MANAGE',
  '/finance/reports': 'FINANCE_REPORT_VIEW',
  '/finance/reports/builder': 'FINANCE_REPORT_VIEW',
  '/finance/setup': 'FINANCE_SETTINGS_MANAGE',
  '/projects': 'PROJECT_VIEW',
  '/projects/list': 'PROJECT_VIEW',
  '/projects/tasks': 'PROJECT_TASK_MANAGE',
  '/projects/boards': 'PROJECT_VIEW',
  '/projects/sprints': 'PROJECT_SPRINT_MANAGE',
  '/projects/timesheets': 'PROJECT_TIMESHEET_MANAGE',
  '/projects/portfolio': 'PROJECT_PORTFOLIO_MANAGE',
  '/projects/issues': 'PROJECT_ISSUE_MANAGE',
  '/projects/gantt': 'PROJECT_VIEW',
  '/projects/resources': 'PROJECT_MANAGE',
  '/projects/calendar': 'PROJECT_VIEW',
  '/projects/workload': 'PROJECT_ANALYTICS_VIEW',
  '/projects/reports': 'PROJECT_ANALYTICS_VIEW',
  '/projects/automation': 'PROJECT_AUTOMATION_MANAGE',
  '/projects/setup': 'PROJECT_MANAGE',
  '/pos/discounts': 'PRODUCT_VIEW',
  '/pos/reports': 'REPORT_VIEW',
  '/pos/terminal-reports': 'REPORT_VIEW',
  '/org/subscription': 'SUBSCRIPTION_MANAGE',
  '/org/settings': 'ORG_MANAGE',
  '/org/administration': 'ORG_MANAGE',
};

const ORG_ROUTE_PERMISSION_RULES = withModuleScopedMasterRoutes(BASE_ORG_ROUTE_PERMISSION_RULES);

export function resolveOrgRoutePermission(pathname: string): string | string[] | null | undefined {
  const adminPerm = resolveModuleAdminRoutePermission(pathname);
  if (adminPerm !== undefined) {
    return adminPerm;
  }
  if (ORG_ROUTE_PERMISSION_RULES[pathname] !== undefined) {
    return ORG_ROUTE_PERMISSION_RULES[pathname];
  }
  const prefix = Object.keys(ORG_ROUTE_PERMISSION_RULES)
    .filter((path) => path !== '/pos' && pathname.startsWith(path + '/'))
    .sort((a, b) => b.length - a.length)[0];
  return prefix ? ORG_ROUTE_PERMISSION_RULES[prefix] : undefined;
}

